// AssetDetailsReduxV2.js - PRD-43 prototype V2
// Incorporates user feedback on V1:
//   - Adds a Technologies metric card (6 total)
//   - Metric cards scroll/link to data-table sections below
//   - Risk trend chart + recent drift events visualization row
//   - Default collapsed: remediation, hardening (moved to tabs)
//   - Bottom tab bar includes all tabs from current AssetDetails.js PLUS
//     Remediation / Hardening / Pentest / Compliance / MITRE
//   - Preserves the existing collapsible data sections verbatim via the
//     new AssetDataSections component

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getAsset,
    getAssetHistory,
    triggerAssetScan,
    getAssetRawDataUrl,
    downloadAssetFile
} from '../../services/assetService';
import { getEvents } from '../../services/driftService';
import { getAssetSuppressions } from '../../services/suppressionService';
import CleanModernLayout from '../../components/layouts/CleanModernLayout';
import MetricsCard from '../../components/prototypes/MetricsCard';
import CollapsibleSection from '../../components/prototypes/CollapsibleSection';
import QuickActions from '../../components/prototypes/QuickActions';
import RiskTrendChart from '../../components/prototypes/RiskTrendChart';
import DriftEventsStrip from '../../components/prototypes/DriftEventsStrip';
import AssetDataSections from '../../components/Assets/AssetDataSections';
import DriftTimeline from '../../components/DriftTimeline';
import DriftEventsList from '../../components/DriftEventsList';
import DriftFindingsTrend from '../../components/DriftFindingsTrend';
import ScanDetailsTab from '../../components/Assets/ScanDetailsTab';
import DeepSecurityTab from '../../components/Assets/DeepSecurityTab';
import './AssetDetailsReduxV2.css';

// ---------- helpers ----------

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return iso;
    }
}

function fmtDateShort(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return iso;
    }
}

function scanScoreSeverity(score) {
    if (score == null || isNaN(score)) return { label: 'UNKNOWN', band: 'neutral' };
    if (score < 30) return { label: 'CRITICAL', band: 'critical' };
    if (score < 50) return { label: 'HIGH', band: 'high' };
    if (score < 70) return { label: 'MEDIUM', band: 'medium' };
    if (score < 85) return { label: 'LOW', band: 'low' };
    return { label: 'HEALTHY', band: 'low' };
}

function vulnDominantBand({ critical = 0, high = 0, medium = 0, low = 0 }) {
    if (critical > 0) return 'critical';
    if (high > 0) return 'high';
    if (medium > 0) return 'medium';
    if (low > 0) return 'low';
    return 'neutral';
}

function priorityTone(priority) {
    if (!priority) return 'neutral';
    const p = String(priority).toLowerCase();
    if (p.includes('critical') || p.includes('p0')) return 'critical';
    if (p.includes('high') || p.includes('p1')) return 'high';
    if (p.includes('medium') || p.includes('p2')) return 'medium';
    if (p.includes('low') || p.includes('p3')) return 'low';
    return 'info';
}

// ---------- AI tab content (reused patterns from V1) ----------

function RemediationTab({ recommendations }) {
    const list = Array.isArray(recommendations) ? recommendations : [];
    if (list.length === 0) {
        return <div className="v2-empty">No prioritized remediations available.</div>;
    }
    return (
        <div className="v2-remediation-list">
            {list.map((rec, i) => {
                const title = rec.title || rec.vulnerability || rec.finding || `Finding ${i + 1}`;
                const priority = rec.priority || rec.severity || null;
                const attackVector = rec.attackVector || rec.vector || null;
                const remediation = rec.remediation || rec.fix || rec.mitigation || null;
                const effort = rec.effort || rec.complexity || null;
                const timeline = rec.timeline || rec.eta || null;
                const cves = rec.cves || rec.cveIds || [];
                return (
                    <div key={i} className="v2-rem-item">
                        <div className="v2-rem-header">
                            <div className="v2-rem-title">{title}</div>
                            {priority && (
                                <span className={`v2-tag v2-tag--${priorityTone(priority)}`}>{priority}</span>
                            )}
                        </div>
                        {attackVector && <div className="v2-rem-field"><span>Attack vector:</span> {attackVector}</div>}
                        {remediation && <div className="v2-rem-field"><span>Remediation:</span> {remediation}</div>}
                        <div className="v2-rem-meta">
                            {effort && <span className="v2-meta-chip">Effort: {effort}</span>}
                            {timeline && <span className="v2-meta-chip">Timeline: {timeline}</span>}
                            {Array.isArray(cves) && cves.length > 0 && (
                                <span className="v2-meta-chip v2-meta-chip--cve">
                                    {cves.slice(0, 4).join(', ')}{cves.length > 4 ? ` +${cves.length - 4}` : ''}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function HardeningTab({ defensive }) {
    if (!defensive) return <div className="v2-empty">No hardening recommendations available.</div>;
    let categories;
    if (Array.isArray(defensive)) {
        categories = [{ name: 'Recommendations', items: defensive }];
    } else if (typeof defensive === 'object') {
        categories = Object.entries(defensive).map(([name, items]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1'),
            items: Array.isArray(items) ? items : [items].filter(Boolean)
        }));
    } else {
        return <div className="v2-empty">No hardening recommendations available.</div>;
    }
    return (
        <div className="v2-hardening">
            {categories.map(({ name, items }, i) => items.length > 0 && (
                <div key={i} className="v2-hardening-card">
                    <h4>{name}</h4>
                    <ul>
                        {items.map((item, j) => (
                            <li key={j}>
                                {typeof item === 'string'
                                    ? item
                                    : (item.recommendation || item.control || item.description || JSON.stringify(item))}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function PentestTab({ roadmap }) {
    if (!roadmap || (typeof roadmap === 'object' && Object.keys(roadmap).length === 0)) {
        return (
            <div className="v2-empty">
                No pentest roadmap available for this asset.
                <div className="v2-empty-hint">
                    Pentest roadmaps are generated by enrichment when the asset has enough discovery data.
                </div>
            </div>
        );
    }
    const phases = [
        { key: 'reconnaissance', label: 'Phase 1: Reconnaissance' },
        { key: 'scanning', label: 'Phase 2: Scanning & Enumeration' },
        { key: 'exploitation', label: 'Phase 3: Exploitation' },
        { key: 'postExploitation', label: 'Phase 4: Post-Exploitation' }
    ];
    const rendered = phases
        .map(({ key, label }) => {
            const phase = roadmap[key] || roadmap[key.replace(/([A-Z])/g, '-$1').toLowerCase()];
            if (!phase) return null;
            const steps = Array.isArray(phase) ? phase : (phase.steps || phase.techniques || [phase]);
            const objective = typeof phase === 'object' && !Array.isArray(phase) ? phase.objective : null;
            return (
                <div key={key} className="v2-phase">
                    <h4>{label}</h4>
                    {objective && <p className="v2-phase-obj">{objective}</p>}
                    <ul>
                        {steps.map((step, j) => (
                            <li key={j}>
                                {typeof step === 'string'
                                    ? step
                                    : (step.technique || step.action || step.description || JSON.stringify(step))}
                            </li>
                        ))}
                    </ul>
                </div>
            );
        })
        .filter(Boolean);

    if (rendered.length === 0) {
        return <div className="v2-empty">No pentest roadmap phases available.</div>;
    }
    return <div className="v2-phases">{rendered}</div>;
}

function ComplianceTab({ compliance }) {
    if (!compliance) return <div className="v2-empty">No compliance mapping available.</div>;
    const frameworks = [
        { key: 'owaspTop10', label: 'OWASP Top 10' },
        { key: 'cisControls', label: 'CIS Controls' },
        { key: 'nistCsf', label: 'NIST CSF' }
    ];
    const content = frameworks
        .map(({ key, label }) => {
            const mapping = compliance[key] || compliance[key.toLowerCase()];
            if (!mapping) return null;
            const isArr = Array.isArray(mapping);
            return (
                <div key={key} className="v2-framework">
                    <h4>{label}</h4>
                    {isArr ? (
                        <ul>
                            {mapping.map((item, i) => (
                                <li key={i}>
                                    {typeof item === 'string'
                                        ? item
                                        : (item.control || item.category || JSON.stringify(item))}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <dl>
                            {Object.entries(mapping).map(([k, v], i) => (
                                <React.Fragment key={i}>
                                    <dt>{k}</dt>
                                    <dd>{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
                                </React.Fragment>
                            ))}
                        </dl>
                    )}
                </div>
            );
        })
        .filter(Boolean);
    if (content.length === 0) return <div className="v2-empty">No compliance mapping available.</div>;
    return <div className="v2-compliance">{content}</div>;
}

function MitreTab({ mitre }) {
    const list = Array.isArray(mitre) ? mitre : [];
    if (list.length === 0) return <div className="v2-empty">No MITRE ATT&amp;CK mapping available.</div>;
    return (
        <div className="v2-mitre-wrap">
            <table className="v2-mitre-table">
                <thead>
                    <tr>
                        <th>Tactic</th>
                        <th>Technique</th>
                        <th>ID</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    {list.map((t, i) => (
                        <tr key={i}>
                            <td>{t.tactic || '—'}</td>
                            <td>{t.technique || t.name || '—'}</td>
                            <td>{t.techniqueId || t.id ? <code>{t.techniqueId || t.id}</code> : '—'}</td>
                            <td>{t.description || t.rationale || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ScanHistoryTab({ history }) {
    const entries = Array.isArray(history)
        ? history
        : (history?.results || history?.snapshots || history?.history || history?.scans || []);
    if (!entries.length) return <div className="v2-empty">No scan history yet.</div>;
    return (
        <div className="v2-history-list">
            {entries.slice(0, 50).map((h, i) => (
                <div key={i} className="v2-history-row">
                    <span className="v2-history-date">{fmtDate(h.completedAt || h.scannedAt || h.timestamp)}</span>
                    <span className="v2-history-type">{h.scanType || h.type || '—'}</span>
                    <span className="v2-history-status">{h.status || '—'}</span>
                    {(h.riskScore != null || h.currentRiskScore != null) && (
                        <span className="v2-history-score">Score: {h.riskScore ?? h.currentRiskScore}</span>
                    )}
                </div>
            ))}
        </div>
    );
}

function TrendsTab({ history, trendData }) {
    return (
        <div className="v2-trends">
            <div className="v2-trends-card">
                <h4>Risk Score Trend</h4>
                <RiskTrendChart data={trendData} />
            </div>
            <div className="v2-trends-card">
                <h4>Findings Trend</h4>
                {history && (history.results || history.scans || []).length > 0 ? (
                    <DriftFindingsTrend
                        data={(history.results || history.scans || []).map((r) => ({
                            date: r.completedAt || r.timestamp,
                            critical: r.vulnCritical || r.criticalCount || 0,
                            high: r.vulnHigh || r.highCount || 0,
                            medium: r.vulnMedium || r.mediumCount || 0,
                            low: r.vulnLow || r.lowCount || 0
                        }))}
                    />
                ) : (
                    <div className="v2-empty">Not enough scan history yet.</div>
                )}
            </div>
        </div>
    );
}

function DriftTabContent({ assetId, history }) {
    const historyEntries = Array.isArray(history)
        ? history
        : (history?.results || history?.snapshots || history?.history || history?.scans || []);
    return (
        <div className="v2-drift">
            <DriftTimeline assetId={assetId} history={historyEntries} />
            <div className="v2-drift-events-wrap">
                <DriftEventsList assetId={assetId} />
            </div>
        </div>
    );
}

function RawDataTab({ asset, assetId }) {
    const resultId = asset?.lastDiscoveryResultId || asset?.lastResultId;
    const [downloading, setDownloading] = useState(null);

    const handleDownload = useCallback(async (fileName) => {
        if (!resultId) return;
        setDownloading(fileName);
        try {
            await downloadAssetFile(assetId, resultId, fileName);
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setDownloading(null);
        }
    }, [assetId, resultId]);

    const handleRaw = useCallback(async () => {
        if (!resultId) return;
        setDownloading('raw');
        try {
            const url = await getAssetRawDataUrl(assetId, resultId);
            if (url) window.open(url, '_blank', 'noopener');
        } catch (err) {
            console.error('Raw download failed:', err);
        } finally {
            setDownloading(null);
        }
    }, [assetId, resultId]);

    if (!resultId) {
        return <div className="v2-empty">No scan results to download yet.</div>;
    }

    const files = [
        { file: 'live_subdomains.txt', label: 'Live subdomains' },
        { file: 'subdomains_all.txt', label: 'All subdomains' },
        { file: 'dns_records.jsonl', label: 'DNS records (JSONL)' },
        { file: 'live_urls.txt', label: 'Live URLs' },
        { file: 'crawled_urls.txt', label: 'Crawled URLs' },
        { file: 'api_urls.txt', label: 'API URLs' },
        { file: 'interesting_urls.txt', label: 'Interesting URLs' },
        { file: 'naabu_results.jsonl', label: 'Port scan results (naabu)' },
        { file: 'nmap_services.json', label: 'Services (nmap)' },
        { file: 'technology_summary.json', label: 'Technology summary' },
        { file: 'cloud_enum.json', label: 'Cloud enumeration' }
    ];

    return (
        <div className="v2-raw">
            <button
                type="button"
                className="v2-raw-download v2-raw-download--primary"
                onClick={handleRaw}
                disabled={downloading === 'raw'}
            >
                {downloading === 'raw' ? 'Preparing…' : 'Download full scan blob (JSON)'}
            </button>
            <div className="v2-raw-grid">
                {files.map(({ file, label }) => (
                    <button
                        key={file}
                        type="button"
                        className="v2-raw-download"
                        onClick={() => handleDownload(file)}
                        disabled={downloading === file}
                    >
                        {downloading === file ? 'Downloading…' : label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ---------- main component ----------

function AssetDetailsReduxV2() {
    const { assetId } = useParams();
    const navigate = useNavigate();
    const dataSectionsAnchorRef = useRef(null);

    const [asset, setAsset] = useState(null);
    const [enrichment, setEnrichment] = useState(null);
    const [history, setHistory] = useState(null);
    const [suppressions, setSuppressions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scanningType, setScanningType] = useState(null);
    const [activeTab, setActiveTab] = useState('scan-history');
    const [scanToast, setScanToast] = useState(null);
    const [driftOpenCount, setDriftOpenCount] = useState(0);

    // Load asset + history
    const loadAsset = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [assetResp, historyResp] = await Promise.all([
                getAsset(assetId),
                getAssetHistory(assetId).catch(() => null)
            ]);
            setAsset(assetResp.asset);
            setEnrichment(assetResp.enrichment || null);
            setHistory(historyResp);
        } catch (err) {
            console.error('Failed to load asset:', err);
            setError(err.message || 'Failed to load asset');
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    useEffect(() => { loadAsset(); }, [loadAsset]);

    // Load suppressions
    useEffect(() => {
        if (!assetId) return;
        (async () => {
            try {
                const result = await getAssetSuppressions(assetId);
                if (result?.success && result.suppressions) {
                    setSuppressions(result.suppressions);
                }
            } catch (err) {
                console.log('Suppressions unavailable:', err.message);
            }
        })();
    }, [assetId]);

    // Load drift open count for metric card
    useEffect(() => {
        if (!assetId) return;
        (async () => {
            try {
                const raw = await getEvents(assetId);
                const list = Array.isArray(raw) ? raw : (raw?.events || []);
                const open = list.filter((e) => e.status === 'open' || e.status === 'acknowledged').length;
                setDriftOpenCount(open);
            } catch (err) {
                console.log('Drift events unavailable:', err.message);
            }
        })();
    }, [assetId]);

    // Trend data for the risk line chart (oldest → newest).
    // History entries expose severity counts but no numeric risk score — derive
    // a severity-weighted score: max(0, 100 - (C*25 + H*10 + M*3 + L*1)).
    const trendData = useMemo(() => {
        if (!history) return [];
        const entries = history.scans || history.results || [];
        return entries
            .slice()
            .sort((a, b) => new Date(a.completedAt || 0) - new Date(b.completedAt || 0))
            .map((r) => {
                const c = r.vulnCritical || 0;
                const h = r.vulnHigh || 0;
                const m = r.vulnMedium || 0;
                const l = r.vulnLow || 0;
                const impact = c * 25 + h * 10 + m * 3 + l * 1;
                return {
                    date: r.completedAt,
                    score: Math.max(0, 100 - impact),
                    level: r.riskLevel || null,
                    critical: c,
                    high: h,
                    medium: m,
                    low: l,
                    total: r.vulnTotal || (c + h + m + l),
                    scanType: r.scanType || null
                };
            })
            .filter((d) => d.date);
    }, [history]);

    // Scan handlers
    const handleScan = useCallback(async (scanType) => {
        setScanningType(scanType);
        setScanToast(null);
        try {
            const res = await triggerAssetScan(assetId, scanType);
            setScanToast({ kind: 'success', msg: res?.message || `${scanType} scheduled.` });
            setTimeout(() => loadAsset(), 600);
        } catch (err) {
            setScanToast({ kind: 'error', msg: err.message || 'Failed to schedule scan' });
        } finally {
            setScanningType(null);
        }
    }, [assetId, loadAsset]);

    const handleExport = useCallback(async (format) => {
        if (format === 'json' && asset?.lastResultId) {
            try {
                const url = await getAssetRawDataUrl(assetId, asset.lastResultId);
                if (url) window.open(url, '_blank', 'noopener');
            } catch (err) {
                setScanToast({ kind: 'error', msg: 'Export failed: ' + err.message });
            }
            return;
        }
        setScanToast({ kind: 'info', msg: `${format.toUpperCase()} export coming soon` });
    }, [assetId, asset]);

    // Scroll to a data section (updates hash, AssetDataSections auto-expands)
    const scrollToSection = useCallback((sectionKey) => {
        // Push the hash — AssetDataSections listens to location.hash
        navigate(`#${sectionKey}`, { replace: true });
        // Also do a direct scroll in case the hash was already set
        const target = document.getElementById(`data-section-${sectionKey}`);
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (dataSectionsAnchorRef.current) {
            dataSectionsAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [navigate]);

    // Derived metric values
    const scanScore = asset?.currentRiskScore ?? null;
    const scanSev = scanScoreSeverity(scanScore);
    const aiScore = asset?.baselineRiskScore ?? enrichment?.baselineRiskScore ?? null;
    const aiLevel = asset?.currentRiskLevel || asset?.baselineRiskLevel || null;
    const vulnCounts = {
        critical: asset?.criticalCount ?? 0,
        high: asset?.highCount ?? 0,
        medium: asset?.mediumCount ?? 0,
        low: asset?.lowCount ?? 0
    };
    const totalVulns = vulnCounts.critical + vulnCounts.high + vulnCounts.medium + vulnCounts.low;
    const vulnBand = vulnDominantBand(vulnCounts);
    const vulnHint = `${vulnCounts.critical}C ${vulnCounts.high}H ${vulnCounts.medium}M ${vulnCounts.low}L`;
    const openPorts = asset?.openPortCount ?? null;
    const subdomainsTotal = asset?.subdomainCount ?? null;
    const subdomainsLive = asset?.liveSubdomainCount ?? null;
    const techCount = asset?.technologyCount ?? null;
    const driftBand = driftOpenCount > 0 ? 'high' : 'low';

    // AI data for bottom tabs
    const ai = enrichment?.aiAnalysis || {};
    const hasDeepSecurity = Boolean(asset?.lastDeepSecurityAt);

    // Handlers for drift-event click
    const handleDriftEventClick = useCallback((event) => {
        setActiveTab('drift');
        // Scroll to the tab panel below
        setTimeout(() => {
            const panel = document.getElementById('v2-tab-panel');
            if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    }, []);

    if (loading) {
        return (
            <CleanModernLayout activePage="assets">
                <div className="v2-wrapper"><div className="v2-loading">Loading asset…</div></div>
            </CleanModernLayout>
        );
    }

    if (error || !asset) {
        return (
            <CleanModernLayout activePage="assets">
                <div className="v2-wrapper">
                    <div className="v2-error">
                        <h2>Asset unavailable</h2>
                        <p>{error || 'Asset not found.'}</p>
                        <button type="button" className="v2-btn" onClick={() => navigate('/assets')}>Back to Assets</button>
                    </div>
                </div>
            </CleanModernLayout>
        );
    }

    return (
        <CleanModernLayout activePage="assets">
            <div className="v2-wrapper">
                <div className="v2-prototype-banner">
                    <span className="v2-prototype-banner__chip">Prototype V2</span>
                    <span>
                        PRD-43 Asset Details Redux — V2 incorporates user feedback (technologies card, metric↔section linking, trend chart, bottom tabs).{' '}
                        <button type="button" className="v2-linklike" onClick={() => navigate(`/asset-details-redux/${assetId}`)}>
                            Compare with V1
                        </button>
                        {' · '}
                        <button type="button" className="v2-linklike" onClick={() => navigate(`/assets/${assetId}`)}>
                            Current page
                        </button>
                    </span>
                </div>

                {/* Header */}
                <header className="v2-header">
                    <div className="v2-header__identity">
                        <div className="v2-header__icon" aria-hidden="true">
                            {asset.assetType === 'ip' || asset.assetType === 'cidr' ? '▣' : '◉'}
                        </div>
                        <div className="v2-header__text">
                            <h1 className="v2-header__name">{asset.value}</h1>
                            <div className="v2-header__meta">
                                <span className="v2-type-badge">{(asset.assetType || 'asset').toUpperCase()}</span>
                                {asset.lastDiscoveryAt && (
                                    <span className="v2-header__timestamp">Last discovered: {fmtDateShort(asset.lastDiscoveryAt)}</span>
                                )}
                                {asset.lastVulnScanAt && (
                                    <span className="v2-header__timestamp">Last vuln scan: {fmtDateShort(asset.lastVulnScanAt)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <QuickActions
                        assetId={assetId}
                        asset={asset}
                        onScan={handleScan}
                        onExport={handleExport}
                        scanInProgress={Boolean(scanningType)}
                    />
                </header>

                {scanToast && (
                    <div className={`v2-toast v2-toast--${scanToast.kind}`} role="status">
                        {scanToast.msg}
                        <button type="button" className="v2-toast__close" onClick={() => setScanToast(null)} aria-label="Dismiss">✕</button>
                    </div>
                )}

                {/* 6-card metrics row */}
                <div className="v2-metrics">
                    <MetricsCard
                        label="Scan Score"
                        value={scanScore != null ? scanScore : '—'}
                        sublabel={scanSev.label}
                        severity={scanSev.band}
                        secondary={aiScore != null ? `AI: ${aiScore}${aiLevel ? ' ' + String(aiLevel).toUpperCase() : ''}` : (aiLevel ? `AI: ${String(aiLevel).toUpperCase()}` : null)}
                    />
                    <MetricsCard
                        label="Vulnerabilities"
                        value={totalVulns}
                        hint={vulnHint}
                        severity={vulnBand}
                        onClick={() => scrollToSection('vulnerabilities')}
                        ariaLabel={`${totalVulns} vulnerabilities, click to view details`}
                    />
                    <MetricsCard
                        label="Open Ports"
                        value={openPorts != null ? openPorts : '—'}
                        severity="neutral"
                        onClick={() => scrollToSection('ports')}
                    />
                    <MetricsCard
                        label="Subdomains"
                        value={subdomainsTotal != null ? subdomainsTotal : '—'}
                        hint={subdomainsLive != null && subdomainsTotal != null ? `${subdomainsLive} live` : undefined}
                        severity="neutral"
                        onClick={() => scrollToSection('subdomains')}
                    />
                    <MetricsCard
                        label="Technologies"
                        value={techCount != null ? techCount : '—'}
                        severity="neutral"
                        onClick={() => scrollToSection('technologies')}
                    />
                    <MetricsCard
                        label="Drift"
                        value={driftOpenCount > 0 ? 'ALERT' : 'Stable'}
                        hint={driftOpenCount > 0 ? `${driftOpenCount} open` : 'No open events'}
                        severity={driftBand}
                        onClick={() => { setActiveTab('drift'); }}
                    />
                </div>

                {/* Visualization row */}
                <div className="v2-viz-row">
                    <div className="v2-viz-card">
                        <div className="v2-viz-card__header">
                            <h3>Risk Score Trend</h3>
                            <span className="v2-viz-card__subtitle">{trendData.length} scan{trendData.length === 1 ? '' : 's'}</span>
                        </div>
                        <RiskTrendChart
                            data={trendData}
                            currentScore={scanScore}
                            baselineScore={aiScore}
                        />
                    </div>
                    <div className="v2-viz-card">
                        <DriftEventsStrip assetId={assetId} limit={5} onEventClick={handleDriftEventClick} />
                    </div>
                </div>

                {/* Attack Surface (collapsible, open) */}
                {ai.attackSurface && (
                    <CollapsibleSection
                        title="Attack Surface Analysis"
                        subtitle="Exposed services, critical technologies, misconfigurations, information leakage"
                        defaultOpen={true}
                    >
                        <AttackSurfaceBlock attackSurface={ai.attackSurface} />
                    </CollapsibleSection>
                )}

                {/* Executive Summary (collapsible, open) */}
                {ai.executiveSummary && (
                    <CollapsibleSection
                        title="Executive Summary"
                        subtitle="AI-generated narrative of the asset's security posture"
                        defaultOpen={true}
                    >
                        <p className="v2-intel-text">{ai.executiveSummary}</p>
                    </CollapsibleSection>
                )}

                {/* Technology Analysis (collapsible, open) */}
                {ai.technologyAnalysis && (
                    <CollapsibleSection
                        title="Technology Analysis"
                        subtitle="Stack overview, risk assessment, end-of-life technologies (PRD-42)"
                        defaultOpen={true}
                    >
                        <TechnologyAnalysisBlock tech={ai.technologyAnalysis} />
                    </CollapsibleSection>
                )}

                {/* Data sections anchor + component (preserves current page details) */}
                <div ref={dataSectionsAnchorRef} id="v2-data-sections-anchor" />
                <AssetDataSections
                    asset={asset}
                    assetId={assetId}
                    enrichment={enrichment}
                    suppressions={suppressions}
                    onSuppressionsChange={setSuppressions}
                />

                {/* Bottom tab bar */}
                <div className="v2-tabs" role="tablist" aria-label="Detailed asset views">
                    <div className="v2-tabs__group" role="none">
                        <span className="v2-tabs__group-label">Data</span>
                        <V2Tab id="scan-history"  label="Scan History"  active={activeTab} onClick={setActiveTab} />
                        <V2Tab id="scan-details"  label="Scan Details"  active={activeTab} onClick={setActiveTab} />
                        <V2Tab id="raw-data"      label="Raw Data"      active={activeTab} onClick={setActiveTab} />
                    </div>
                    <div className="v2-tabs__group" role="none">
                        <span className="v2-tabs__group-label">Activity</span>
                        <V2Tab id="trends"  label="Trends"  active={activeTab} onClick={setActiveTab} />
                        <V2Tab id="drift"   label="Drift"   active={activeTab} onClick={setActiveTab} />
                    </div>
                    <div className="v2-tabs__group" role="none">
                        <span className="v2-tabs__group-label">AI Guidance</span>
                        <V2Tab id="remediation" label="Remediation"  active={activeTab} onClick={setActiveTab} />
                        <V2Tab id="hardening"   label="Hardening"    active={activeTab} onClick={setActiveTab} />
                        <V2Tab id="pentest"     label="Pentest"      active={activeTab} onClick={setActiveTab} />
                        <V2Tab id="compliance"  label="Compliance"   active={activeTab} onClick={setActiveTab} />
                        <V2Tab id="mitre"       label="MITRE ATT&CK" active={activeTab} onClick={setActiveTab} />
                    </div>
                    {hasDeepSecurity && (
                        <div className="v2-tabs__group" role="none">
                            <span className="v2-tabs__group-label">Deep</span>
                            <V2Tab id="deep-security" label="Deep Security" active={activeTab} onClick={setActiveTab} />
                        </div>
                    )}
                </div>

                <div id="v2-tab-panel" className="v2-tab-panel" role="tabpanel">
                    {activeTab === 'scan-history' && <ScanHistoryTab history={history} />}
                    {activeTab === 'scan-details' && <ScanDetailsTab assetId={assetId} history={history} />}
                    {activeTab === 'raw-data'     && <RawDataTab asset={asset} assetId={assetId} />}
                    {activeTab === 'trends'       && <TrendsTab history={history} trendData={trendData} />}
                    {activeTab === 'drift'        && <DriftTabContent assetId={assetId} history={history} />}
                    {activeTab === 'remediation'  && <RemediationTab recommendations={ai.prioritizedRecommendations} />}
                    {activeTab === 'hardening'    && <HardeningTab defensive={ai.defensiveRecommendations} />}
                    {activeTab === 'pentest'      && <PentestTab roadmap={ai.penetrationTestingRoadmap} />}
                    {activeTab === 'compliance'   && <ComplianceTab compliance={ai.complianceMapping} />}
                    {activeTab === 'mitre'        && <MitreTab mitre={ai.mitreAttackMapping || ai.mitreMapping} />}
                    {activeTab === 'deep-security' && hasDeepSecurity && <DeepSecurityTab asset={asset} assetId={assetId} />}
                </div>
            </div>
        </CleanModernLayout>
    );
}

function V2Tab({ id, label, active, onClick }) {
    const isActive = active === id;
    return (
        <button
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`v2-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => onClick(id)}
        >
            {label}
        </button>
    );
}

function AttackSurfaceBlock({ attackSurface }) {
    if (!attackSurface) return null;
    const {
        exposedServices = [],
        criticalTechnologies = [],
        misconfigurations = [],
        informationLeakage = [],
        summary
    } = attackSurface;
    return (
        <div>
            {summary && <p className="v2-intel-text v2-intel-text--lede">{summary}</p>}
            <div className="v2-attack-grid">
                {[
                    { title: 'Exposed Services', items: exposedServices },
                    { title: 'Critical Technologies', items: criticalTechnologies },
                    { title: 'Misconfigurations', items: misconfigurations },
                    { title: 'Information Leakage', items: informationLeakage }
                ].map(({ title, items }) => items.length > 0 && (
                    <div key={title} className="v2-attack-card">
                        <h4>{title}</h4>
                        <ul>
                            {items.map((it, i) => (
                                <li key={i}>
                                    {typeof it === 'string'
                                        ? it
                                        : (it.name || it.service || it.technology || it.description || it.issue || JSON.stringify(it))}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TechnologyAnalysisBlock({ tech }) {
    if (!tech) return null;
    return (
        <div>
            {tech.techHealthScore != null && (
                <div className="v2-stat-line">
                    <span className="v2-stat-label">Tech Health Score:</span>
                    <span className="v2-stat-value">{tech.techHealthScore}</span>
                </div>
            )}
            {tech.stackOverview && <p className="v2-intel-text">{tech.stackOverview}</p>}
            {tech.riskAssessment && (
                <>
                    <h4>Risk Assessment</h4>
                    <p className="v2-intel-text">{tech.riskAssessment}</p>
                </>
            )}
            {Array.isArray(tech.eolTechnologies) && tech.eolTechnologies.length > 0 && (
                <>
                    <h4>End-of-Life Technologies</h4>
                    <ul>
                        {tech.eolTechnologies.map((t, i) => (
                            <li key={i}>
                                {typeof t === 'string'
                                    ? t
                                    : `${t.name || t.technology || 'Unknown'}${t.version ? ' ' + t.version : ''}${t.notes ? ' — ' + t.notes : ''}`}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

export default AssetDetailsReduxV2;
